// src/data/books/FundamentosBlockchain/data/cap12.js
const BLOCKCHAIN_FUNDAMENTOS_CH12 = {
  id: "blockchain-fundamentos-12",
  slug: "inmutabilidad",
  contentId: 12,
  title: "Inmutabilidad en Blockchain: Garantía de Integridad y Confianza Permanente",
  sections: [
    {
      id: "introduccion",
      heading: "El valor de la inmutabilidad en los sistemas digitales",
      text: `La **inmutabilidad** es uno de los pilares fundamentales que distinguen a blockchain de cualquier otra tecnología de registro digital. 
En un entorno donde la manipulación o alteración de datos puede tener consecuencias económicas, legales y sociales, 
blockchain introduce un principio revolucionario: una vez que la información se valida y se agrega a la cadena, **no puede modificarse ni eliminarse**. 
Este rasgo otorga a la red un nivel de integridad y confiabilidad sin precedentes, 
haciendo que los registros sean permanentes, verificables y resistentes a la censura.`
    },
    {
      id: "concepto",
      heading: "¿Qué significa que un dato sea inmutable?",
      text: `La inmutabilidad implica que, una vez registrado, un bloque de información no puede ser alterado sin invalidar toda la secuencia de bloques que lo sigue. 
Cada bloque en la cadena está vinculado criptográficamente al anterior mediante un **hash**, 
una firma matemática única derivada de su contenido. 
Si alguien intentara cambiar un solo carácter dentro de un bloque, 
el hash resultante sería completamente distinto, rompiendo la conexión con los bloques posteriores y alertando inmediatamente a la red.`
    },
    {
      id: "consenso-y-mayoria",
      heading: "El rol del consenso y el 51%",
      text: `En teoría, una modificación en blockchain sería posible solo si **más del 51% de los nodos** de la red decidieran aceptar la versión alterada. 
Este fenómeno se conoce como **ataque del 51%**, 
y representaría un intento coordinado de controlar la mayoría del poder de cómputo o participación en la red. 
Sin embargo, en las principales blockchains públicas, 
como Bitcoin o Ethereum, esta posibilidad es prácticamente inviable debido a la magnitud, el costo y la coordinación que requeriría. 

El consenso distribuido actúa, por tanto, como un escudo colectivo: 
la veracidad de los datos no depende de la buena fe de un actor, sino del acuerdo entre miles de participantes independientes.`
    },
    {
      id: "estructura-hash",
      heading: "Cómo los hashes garantizan la integridad",
      text: `Cada bloque en una cadena contiene tres elementos clave:  
1. **Los datos registrados** (transacciones, contratos, etc.).  
2. **El hash del bloque anterior**, que actúa como enlace con el pasado.  
3. **Su propio hash**, generado a partir del contenido interno del bloque.  

Esta estructura encadenada garantiza que cualquier cambio en los datos alteraría su hash y, en consecuencia, 
rompería la relación con los bloques siguientes. 
La red detectaría de inmediato la discrepancia y rechazaría la versión manipulada. 
En otras palabras, los hashes son la “huella digital” matemática que asegura que el historial permanezca intacto.`
    },
    {
      id: "ejemplo-practico",
      heading: "Ejemplo práctico de inmutabilidad",
      text: `Imaginemos que una transacción ya registrada en la red Bitcoin intenta ser modificada por un atacante para cambiar el monto transferido. 
Para lograrlo, tendría que reescribir ese bloque, recalcular su hash y rehacer todos los bloques posteriores, 
además de convencer a la mayoría de los nodos de aceptar la nueva versión como válida. 
El enorme poder computacional y tiempo necesarios para esta tarea hacen que sea **económicamente inviable**. 
Por eso, la inmutabilidad de blockchain se considera una forma de seguridad basada en la **imposibilidad práctica de alteración**, 
más que en una prohibición técnica.`
    },
    {
      id: "aplicaciones",
      heading: "Aplicaciones prácticas de la inmutabilidad",
      text: `La inmutabilidad convierte a blockchain en una herramienta ideal para casos donde la integridad histórica de los datos es crítica:
      
- **Registros financieros:** auditorías automáticas y trazabilidad de movimientos contables.  
- **Propiedad digital:** títulos, certificaciones y derechos de autor inviolables.  
- **Identidad y reputación:** datos personales protegidos contra alteración indebida.  
- **Cadenas de suministro:** historial incorruptible del recorrido de productos.  
- **Gobierno y notaría pública:** documentos y votaciones imposibles de manipular.`
    },
    {
      id: "inmutabilidad-2025",
      heading: "Inmutabilidad y evolución tecnológica hacia 2025",
      text: `En 2025, la inmutabilidad blockchain se ha reforzado mediante nuevos mecanismos de verificación y respaldo criptográfico. 
Las redes modernas emplean **Merkle Trees** más eficientes, **almacenamiento distribuido interplanetario (IPFS)** y técnicas de **timestamping cuántico**, 
que registran la existencia de un dato en un momento exacto de forma irrefutable. 

También han surgido soluciones híbridas que combinan blockchain con almacenamiento fuera de cadena (off-chain), 
permitiendo conservar grandes volúmenes de información sin perder trazabilidad ni integridad. 
Estas innovaciones aseguran que incluso los datos almacenados fuera de la cadena mantengan una referencia verificable y permanente en el libro mayor.`
    },
    {
      id: "ventajas",
      heading: "Ventajas de la inmutabilidad en blockchain",
      text: `- **Integridad garantizada:** los registros no pueden modificarse sin romper la cadena de validación.  
- **Confianza matemática:** la verificación reemplaza la fe institucional.  
- **Auditoría permanente:** todo el historial está disponible para revisión en cualquier momento.  
- **Seguridad frente a fraudes:** las manipulaciones se detectan de inmediato.  
- **Valor legal y probatorio:** los registros inmutables sirven como evidencia verificable ante terceros.`
    },
    {
      id: "limitaciones",
      heading: "Limitaciones y malentendidos comunes",
      text: `Aunque la inmutabilidad es una fortaleza, no debe interpretarse como rigidez absoluta. 
Los datos erróneos pueden corregirse, pero no eliminándose, sino agregando nuevas transacciones que anulan o reemplazan las anteriores. 
Esto mantiene la trazabilidad completa de los cambios sin alterar el historial original. 
Asimismo, la inmutabilidad no implica anonimato: si las claves públicas se asocian con identidades reales, 
las transacciones pueden volverse rastreables, lo que requiere estrategias adicionales de privacidad.`
    },
    
  ]
};

export default BLOCKCHAIN_FUNDAMENTOS_CH12;
